import { ServerError } from '@/types/errors/server.error';

export const METHOD = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
} as const;

export type Method = (typeof METHOD)[keyof typeof METHOD];

interface RequestParams {
  url: string;
  method: Method;
  body?: BodyInit;
  headers?: Record<string, string>;
  token?: string;
}

interface JsonRequestParams {
  url: string;
  method: Method;
  body?: unknown;
  headers?: Record<string, string>;
  token?: string;
}

interface FormDataRequestParams extends RequestParams {
  body: FormData;
}

export const sendRequest = async ({ url, method, body, headers, token }: RequestParams) => {
  const defaultHeaders = { Authorization: `Bearer ${token ?? import.meta.env.VITE_TESTING_TOKEN}` };
  const requestHeaders = { ...defaultHeaders, ...headers };
  const response = await fetch(url, {
    method,
    body,
    headers: requestHeaders,
    redirect: 'follow',
    cache: method === METHOD.GET ? 'no-store' : undefined,
  });
  return handleResponse(response);
};

export const sendJSONRequest = async ({ url, method, body, headers, token }: JsonRequestParams) => {
  const hasBody = body !== undefined;
  const defaultHeaders: Record<string, string> = {
    Authorization: `Bearer ${token ?? import.meta.env.VITE_TESTING_TOKEN}`,
  };
  if (hasBody) {
    defaultHeaders['Content-Type'] = 'application/json';
  }
  const requestHeaders = { ...defaultHeaders, ...headers };
  const response = await fetch(url, {
    method,
    body: hasBody ? JSON.stringify(body) : undefined,
    headers: requestHeaders,
    cache: method === METHOD.GET ? 'no-store' : undefined,
  });
  return handleJSONResponse(response);
};

export const sendFormDataRequest = async ({ url, method, body, headers, token }: FormDataRequestParams) => {
  const defaultHeaders = { Authorization: `Bearer ${token ?? import.meta.env.VITE_TESTING_TOKEN}` };
  const requestHeaders = { ...defaultHeaders, ...headers };
  const response = await fetch(url, { method, body, headers: requestHeaders });
  return handleJSONResponse(response);
};

const handleResponse = async (response: Response) => {
  if (response.status === 500) {
    let message = 'Something went wrong. Please try again later.';
    try {
      const body = await response.clone().json();
      if (body?.message && typeof body.message === 'string') {
        message = body.message;
      }
    } catch {
      // Response body is not JSON, use default message
    }
    throw new Error(message);
  }

  if (!response.ok) {
    const parsedResponse = await response.json();
    throw new ServerError({
      message: resolveServerErrorMessage(parsedResponse.message),
      statusCode: response.status,
      errorCode: parsedResponse.errorCode,
      details: parsedResponse?.details,
    });
  }
  return response;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleJSONResponse = async (response: Response): Promise<any> => {
  // 204 and other empty successful DELETE/PUT responses must not call response.json().
  if (response.status === 204 || response.status === 205) {
    return undefined;
  }

  const res = await handleResponse(response);
  const text = await res.text();
  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Unexpected non-JSON response from server');
  }
};

/**
 * Nest sends a string, a validation array, or `{ message }` (possibly nested).
 * Keep a string for ServerError, including joined validation entries.
 */
export function resolveServerErrorMessage(raw: unknown): string {
  return collectServerErrorMessage(raw) ?? 'Unexpected error';
}

function collectServerErrorMessage(raw: unknown): string | undefined {
  if (typeof raw === 'string') {
    return raw.length > 0 ? raw : undefined;
  }
  if (Array.isArray(raw)) {
    const messages = raw
      .map((entry) => collectServerErrorMessage(entry))
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join(', ') : undefined;
  }
  if (raw && typeof raw === 'object' && 'message' in raw) {
    return collectServerErrorMessage((raw as { message: unknown }).message);
  }
  return undefined;
}
